// Cost management screen (admin console screen 3 of 5) — route
// /cost-management, RequireRole roles={['admin']} in App.jsx. Reads the
// SAME GET /admin/catalog/products endpoint useAdminCatalog.js (screen 2,
// Catalog) reads — adminCatalogRoutes.js's own comment: "admin is allowed
// to see cost data," the two screens just render different columns from
// one admin-only read.
//
// Cost boundary / file isolation (this task's own brief, and root
// CLAUDE.md rule #1 "Sales must never see cost price"): this is a
// DELIBERATELY SEPARATE hook file from useAdminCatalog.js, with its own
// query-key prefix and its own grouping function (groupCostRows below),
// not imported from or exported to useAdminCatalog.js. That file's
// groupCatalogRows() drops cost_price/supplier_name/cost_updated_at on
// purpose; this file's whole job is to keep them. Nothing in this file is
// ever imported by CatalogPage.jsx, useAdminCatalog.js, or any
// sales-reachable file — grep this repo's sales-facing files for
// "cost_price"/"supplier_name"/"cost_updated_at"/"costPrice"/
// "supplierName"/"costUpdatedAt" to confirm before trusting that claim
// again after any future edit.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from 'shared/api/client.js';

const COST_PRODUCTS_KEY_PREFIX = ['admin', 'costManagementProducts'];

const productsKey = (search) => [...COST_PRODUCTS_KEY_PREFIX, search ?? null];

// One row per (product, tier) pair from the LEFT JOIN product_prices/
// product_costs join (productRepository.getProductsForAdmin) — collapsed to
// one row per product here, same "first occurrence wins" shape as
// useAdminCatalog.js's groupCatalogRows(), but keeping cost_price/
// supplier_name/cost_updated_at instead of dropping them. Price tiers
// aren't needed on this screen at all, so they're never even read off the
// row.
function groupCostRows(rows) {
  const map = new Map();
  for (const row of rows) {
    if (!map.has(row.id)) {
      map.set(row.id, {
        id: row.id,
        sku: row.sku,
        name: row.name,
        nameAm: row.name_am,
        costPrice: row.cost_price,
        supplierName: row.supplier_name,
        costUpdatedAt: row.cost_updated_at,
      });
    }
  }
  return Array.from(map.values());
}

// GET /admin/catalog/products?search= — no category filter on this screen
// (this task's own scope: sku/name/cost/supplier/staleness, not full
// catalog browsing — CatalogPage already owns category filtering).
// Active products only, same p.is_active = 1 filter every catalog read
// applies.
export function useAdminCostManagementProducts({ search } = {}) {
  return useQuery({
    queryKey: productsKey(search),
    queryFn: () => apiClient.get('/admin/catalog/products', { search }),
    select: (data) => groupCostRows(data.products),
  });
}

function updateAllCostCaches(queryClient, updater) {
  queryClient.setQueriesData({ queryKey: COST_PRODUCTS_KEY_PREFIX }, (prev) => {
    if (!prev || !prev.products) return prev;
    return { ...prev, products: updater(prev.products) };
  });
}

function invalidateCostProducts(queryClient) {
  queryClient.invalidateQueries({ queryKey: COST_PRODUCTS_KEY_PREFIX });
}

// PUT /admin/catalog/products/:id/cost -> { cost: { product_id, cost_price,
// supplier_name, cost_updated_at, updated_by } } (catalogService.upsertCost's
// own return shape — the full product_costs row, raw snake_case, same as
// every other repository read in this codebase). Merges the new cost fields
// onto every raw (pre-select) row sharing this product id across every
// cached search variant, then invalidates for a real reconcile — same
// pattern as useAdminCatalog.js's useUpdateProduct/useUpsertPriceTier.
export function useUpdateProductCost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => apiClient.put(`/admin/catalog/products/${id}/cost`, body),
    onSuccess: (data) => {
      const cost = data.cost;
      updateAllCostCaches(queryClient, (rows) =>
        rows.map((row) =>
          row.id === cost.product_id
            ? {
                ...row,
                cost_price: cost.cost_price,
                supplier_name: cost.supplier_name,
                cost_updated_at: cost.cost_updated_at,
              }
            : row
        )
      );
      invalidateCostProducts(queryClient);
    },
  });
}

export default useAdminCostManagementProducts;
